/**
 * useNudgeDelivery
 *
 * Reusable hook that checks whether a given nudge should be shown based on
 * server-side delivery tracking (NudgeDeliveryStore Durable Object).
 *
 * Usage:
 *   const { shouldShow, recordImpression, isLoading } = useNudgeDelivery(
 *     'booking_link_share',  // stable nudge ID
 *     3,                     // max times to show
 *     { cooldownMs: 60 * 60 * 1000 }  // optional: 1-hour cooldown between shows
 *   );
 *
 *   useEffect(() => {
 *     if (shouldShow) recordImpression(); // call once when nudge is displayed
 *   }, [shouldShow]);
 */

import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nudgeDeliveryApi } from '@/api';

// ---------------------------------------------------------------------------
// localStorage helpers — persist cooldown locally so page reloads respect it
// even when the API is unavailable (e.g. local dev before DO migration runs).
// ---------------------------------------------------------------------------
function lsKey(nudgeId: string) {
  return `tilly_nudge_${nudgeId}_lastShown`;
}
function readLocalLastShown(nudgeId: string): number | null {
  try {
    const raw = localStorage.getItem(lsKey(nudgeId));
    return raw ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
}
function writeLocalLastShown(nudgeId: string, ts: number) {
  try {
    localStorage.setItem(lsKey(nudgeId), String(ts));
  } catch {
    // storage full or blocked — silently ignore
  }
}

export interface UseNudgeDeliveryOptions {
  /** Minimum ms between impressions. Default: 0 (no cooldown). */
  cooldownMs?: number;
}

export interface UseNudgeDeliveryResult {
  /** Whether this nudge should currently be displayed */
  shouldShow: boolean;
  /** Call this once when the nudge becomes visible */
  recordImpression: () => void;
  /** True while the delivery record is being fetched */
  isLoading: boolean;
  /** How many times this nudge has already been shown (0 if never) */
  showCount: number;
}

const ONE_HOUR_MS = 60 * 60 * 1000;

export function useNudgeDelivery(
  nudgeId: string,
  maxShows: number,
  { cooldownMs = ONE_HOUR_MS }: UseNudgeDeliveryOptions = {}
): UseNudgeDeliveryResult {
  const queryClient = useQueryClient();
  const queryKey = ['nudgeDelivery', nudgeId];

  // Session-local flag: once shown, suppress for this component lifetime
  // regardless of server state. Prevents re-shows when API calls fail or
  // when recordImpression causes re-renders.
  const hasShownThisSessionRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => nudgeDeliveryApi.get(nudgeId),
    staleTime: 60_000,
    retry: false,
  });

  const delivery = data?.delivery;
  const showCount = delivery?.showCount ?? 0;

  // Use the later of server lastShownAt and locally-cached lastShownAt so the
  // cooldown survives page reloads even when the API is unavailable.
  const serverLastShown = delivery?.lastShownAt ?? null;
  const localLastShown = readLocalLastShown(nudgeId);
  const lastShownAt =
    serverLastShown !== null && localLastShown !== null
      ? Math.max(serverLastShown, localLastShown)
      : (serverLastShown ?? localLastShown);

  const withinCooldown =
    // eslint-disable-next-line react-hooks/purity
    cooldownMs > 0 && lastShownAt !== null && Date.now() - lastShownAt < cooldownMs;

  const shouldShow =
    !isLoading && !hasShownThisSessionRef.current && showCount < maxShows && !withinCooldown;

  const mutation = useMutation({
    mutationFn: () => nudgeDeliveryApi.record(nudgeId),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, { delivery: result.delivery });
    },
  });

  // Keep a stable ref to the mutation so recordImpression never changes identity.
  // Changing identity would cause consumer useEffects to re-run and re-show.
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  const recordImpression = useCallback(() => {
    // Write to localStorage immediately — this persists the cooldown across
    // reloads regardless of whether the server call succeeds.
    writeLocalLastShown(nudgeId, Date.now());
    // Set the session flag so shouldShow flips false right away.
    hasShownThisSessionRef.current = true;
    if (!mutationRef.current.isPending) {
      mutationRef.current.mutate();
    }
  }, [nudgeId]); // nudgeId is stable; all other refs are intentionally excluded

  return { shouldShow, recordImpression, isLoading, showCount };
}
